/* eslint-disable max-lines */
/**
 * Texture option definitions for sky and floor textures.
 *
 * These constants define available texture options for the 3D renderer.
 * Sky textures support multiple resolution variants that are automatically
 * selected based on the render quality setting.
 *
 * ## Progressive Texture Loading
 *
 * The texture system uses a phased loading approach:
 * 1. **Phase 1 (Critical)**: Only the selected floor + sky load initially
 * 2. **Phase 2-3 (Scene)**: Scene-specific textures load in parallel
 * 3. **Phase 5-7 (Background)**: Other textures preload for instant switching
 *
 * ## Sky Texture Quality Variants
 *
 * Sky textures can have multiple resolution variants. The renderer automatically
 * selects the appropriate variant based on `renderQuality`:
 *
 * | Quality | Resolution | Use Case |
 * |---------|------------|----------|
 * | 0-1     | None       | Wireframe/minimal |
 * | 2-6     | 2K (default) | Mobile/balanced |
 * | 7-8     | 4K         | Desktop/high quality |
 * | 9       | 8K         | Ultra quality |
 * | 10      | 16K        | Maximum quality |
 *
 * Default (2k) textures load immediately; higher resolutions upgrade via fade.
 *
 * @example Adding a new sky with quality variants:
 * ```typescript
 * {
 *   key: 'sky/my-sky',
 *   name: 'My Custom Sky',
 *   description: 'A beautiful custom sky environment',
 *   texture: 'my_sky_2k.hdr',  // Default/fallback texture
 *   textures: {
 *     '2k': 'my_sky_2k.hdr',   // Render quality 2-6
 *     '4k': 'my_sky_4k.hdr',   // Render quality 7-8
 *     '8k': 'my_sky_8k.hdr',   // Render quality 9
 *     '16k': 'my_sky_16k.hdr'  // Render quality 10
 *   }
 * }
 * ```
 *
 * @module texture-options
 */

/**
 * Resolution variants for sky textures.
 *
 * Each key represents a resolution tier that maps to render quality levels.
 * The renderer falls back to lower resolutions if a higher one is unavailable.
 *
 * @property 2k - Medium quality (render quality 2-6), ~2-4 MB
 * @property 4k - High quality (render quality 7-8), ~8-12 MB
 * @property 8k - Very-high quality (render quality 9), ~25-35 MB
 * @property 16k - Ultra-high quality (render quality 10), ~80-100 MB
 */
export interface SkyResolutionVariants {
	/** Medium quality texture (render quality 2-6) */
	'2k'?: string;
	/** High quality texture (render quality 7-8) */
	'4k'?: string;
	/** Very-high quality texture (render quality 9) */
	'8k'?: string;
	/** Ultra-high quality texture (render quality 10) */
	'16k'?: string;
}

/**
 * Texture option definition for sky and floor textures.
 *
 * @property key - Unique identifier used in API (e.g., 'sky/overcast', 'floor/grid-tile')
 * @property name - Human-readable display name
 * @property description - Description of the texture appearance/purpose
 * @property texture - Default texture filename (relative to textures/ folder)
 * @property textures - Optional resolution variants for quality-based selection (sky only)
 */
export interface TextureOption {
	/** Unique identifier (e.g., 'sky/overcast', 'floor/grid-tile') */
	key: string;
	/** Human-readable display name */
	name: string;
	/** Description of texture appearance */
	description: string;
	/** Default texture filename */
	texture: string;
	/** PNG thumbnail filename for preview display */
	thumbnail?: string;
	/**
	 * Optional resolution variants for quality-based texture selection.
	 * If not provided, the `texture` field is used for all quality levels.
	 * Higher resolution variants are loaded asynchronously and faded in.
	 */
	textures?: SkyResolutionVariants;
	/**
	 * Optional KTX2/BasisU compressed variant of `texture`. When present and
	 * KTX2 is supported on the device, this is loaded in place of `texture`,
	 * eliminating the RGBA8 decode transient that iOS Safari counts against
	 * the tab memory budget.
	 */
	ktx2Texture?: string;
	/**
	 * Optional KTX2 resolution variants paralleling `textures`. Use when a
	 * sky entry has KTX2 variants for multiple quality tiers.
	 */
	ktx2Textures?: SkyResolutionVariants;
	/**
	 * Sky texture key to use for the UV reflection/environment map.
	 *
	 * - `''`, `null`, or `undefined` — legacy behavior: uses the current sky
	 *   texture for reflections. Quality upgrades apply to both background and reflections.
	 * - Valid sky key (e.g. `'sky/rainforest_trail'`) — uses that sky entry's base
	 *   `texture` value for the reflection environment map. The reflection texture
	 *   is NOT upgraded based on render quality; only the background sky upgrades.
	 */
	uvLayer?: string;
}

/**
 * Available sky texture options.
 *
 * Use these keys with the `skyKey` prop in RenderKitchenProps or
 * via `controller.changeSkyTexture(key)`.
 *
 * Sky textures are HDR environment maps that provide both background
 * imagery and environment lighting for reflections.
 *
 * @example
 * ```typescript
 * // In React component
 * <Kitchen3d skyKey="sky/tiergarten" />
 *
 * // Or via controller
 * controller.changeSkyTexture('sky/tiergarten');
 * ```
 */
export const SKY_TEXTURES: TextureOption[] = [
	{
		key: 'background',
		name: 'Solid Background',
		description: 'Uses the backgroundColor prop as a solid color background',
		texture: '',
		uvLayer: 'sky/under_tower'
	},
	{
		key: 'sky/overcast',
		name: 'Overcast',
		description: 'Soft overcast sky with diffused lighting',
		texture: 'kloofendal_misty_morning_puresky_1k.hdr',
		thumbnail: 'kloofendal_misty_morning_puresky.hdr.jpeg',
		textures: {
			'2k': 'kloofendal_misty_morning_puresky_2k.hdr',
			'4k': 'kloofendal_misty_morning_puresky_4k.hdr',
			'8k': 'kloofendal_misty_morning_puresky_8k.hdr'
		},
		uvLayer: 'sky/under_tower'
	},
	{
		key: 'sky/pure-sky',
		name: 'Pure Sky',
		description: '',
		texture: 'citrus_orchard_road_puresky_1k.hdr',
		thumbnail: 'citrus_orchard_road_puresky.hdr.jpeg',
		textures: {
			'2k': 'citrus_orchard_road_puresky_2k.hdr',
			'4k': 'citrus_orchard_road_puresky_4k.hdr',
			'8k': 'citrus_orchard_road_puresky_8k.hdr'
		},
		uvLayer: 'sky/under_tower'
	},
	{
		key: 'sky/sunrise',
		name: 'Sunrise',
		description: 'Warm sunrise HDR environment with golden hour lighting',
		texture: 'spruit_sunrise_1k.hdr',
		thumbnail: 'spruit_sunrise.hdr.jpeg',
		textures: {
			'2k': 'spruit_sunrise_2k.hdr',
			'4k': 'spruit_sunrise_4k.hdr',
			'8k': 'spruit_sunrise_8k.hdr'
		},
		uvLayer: 'sky/under_tower'
	},
	{
		key: 'sky/beach',
		name: 'Beach Sunrise',
		description: 'Coastal beach scene at sunrise with ocean reflections',
		texture: 'blouberg_sunrise_2_1k.hdr',
		thumbnail: 'blouberg_sunrise_2.hdr.jpeg',
		textures: {
			'2k': 'blouberg_sunrise_2_2k.hdr',
			'4k': 'blouberg_sunrise_2_4k.hdr',
			'8k': 'blouberg_sunrise_2_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/under_tower',
		name: 'Under Tower',
		description: 'Paris scene from beneath the Eiffel Tower',
		texture: 'little_paris_under_tower_1k.hdr',
		thumbnail: 'little_paris_under_tower.hdr.jpeg',
		textures: {
			'2k': 'little_paris_under_tower_2k.hdr',
			'4k': 'little_paris_under_tower_4k.hdr',
			'8k': 'little_paris_under_tower_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/qwantani_patio',
		name: 'Qwantani Patio',
		description: 'Outdoor patio environment with natural lighting',
		texture: 'qwantani_patio_1k.hdr',
		thumbnail: 'qwantani_patio.hdr.jpeg',
		textures: {
			'2k': 'qwantani_patio_2k.hdr',
			'4k': 'qwantani_patio_4k.hdr',
			'8k': 'qwantani_patio_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/rainforest_trail',
		name: 'Rainforest Trail',
		description: 'Lush rainforest trail with dappled green lighting',
		texture: 'rainforest_trail_1k.hdr',
		thumbnail: 'rainforest_trail.hdr.jpeg',
		textures: {
			'2k': 'rainforest_trail_2k.hdr',
			'4k': 'rainforest_trail_4k.hdr',
			'8k': 'rainforest_trail_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/studio_garden',
		name: 'Studio Garden',
		description: 'Garden studio setting with balanced natural light',
		texture: 'studio_garden_1k.hdr',
		thumbnail: 'studio_garden.hdr.jpeg',
		textures: {
			'2k': 'studio_garden_2k.hdr',
			'4k': 'studio_garden_4k.hdr',
			'8k': 'studio_garden_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/little_paris',
		name: 'Little Paris',
		description: 'Paris cityscape with Eiffel Tower in view',
		texture: 'little_paris_eiffel_tower_1k.hdr',
		thumbnail: 'little_paris_eiffel_tower.hdr.jpeg',
		textures: {
			'2k': 'little_paris_eiffel_tower_2k.hdr',
			'4k': 'little_paris_eiffel_tower_4k.hdr',
			'8k': 'little_paris_eiffel_tower_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/meadow',
		name: 'Meadow',
		description: 'Open meadow with clear sky and natural grass surroundings',
		texture: 'meadow_1k.hdr',
		thumbnail: 'meadow.hdr.jpeg',
		textures: {
			'2k': 'meadow_2k.hdr',
			'4k': 'meadow_4k.hdr',
			'8k': 'meadow_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/symmetrical_garden',
		name: 'Symmetrical Garden',
		description: 'Formal garden with symmetrical landscaping',
		texture: 'symmetrical_garden_02_1k.hdr',
		thumbnail: 'symmetrical_garden_02.hdr.jpeg',
		textures: {
			'2k': 'symmetrical_garden_02_2k.hdr',
			'4k': 'symmetrical_garden_02_4k.hdr',
			'8k': 'symmetrical_garden_02_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/pretoria_gardens',
		name: 'Pretoria Gardens',
		description: 'Botanical garden setting with varied foliage',
		texture: 'pretoria_gardens_1k.hdr',
		thumbnail: 'pretoria_gardens.hdr.jpeg',
		textures: {
			'2k': 'pretoria_gardens_2k.hdr',
			'4k': 'pretoria_gardens_4k.hdr',
			'8k': 'pretoria_gardens_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/ballawley_park',
		name: 'Ballawley Park',
		description: 'Park environment with mature trees and open lawn',
		texture: 'ballawley_park_1k.hdr',
		thumbnail: 'ballawley_park.hdr.jpeg',
		textures: {
			'2k': 'ballawley_park_2k.hdr',
			'4k': 'ballawley_park_4k.hdr',
			'8k': 'ballawley_park_8k.hdr'
		},
		uvLayer: ''
	},
	{
		key: 'sky/tiergarten',
		name: 'Tiergarten',
		description: 'A clear midday garden courtyard with strong sunlight, high-contrast tree shadows, warm brick highlights and grassy areas.',
		texture: 'tiergarten_1k.hdr',
		thumbnail: 'tiergarten.hdr.jpeg',
		textures: {
			'2k': 'tiergarten_2k.hdr',
			'4k': 'tiergarten_4k.hdr',
			'8k': 'tiergarten_8k.hdr'
			// '16k': 'tiergarten_16k.hdr'
			// Add '4k': 'tiergarten_4k.hdr' when file available
		},
		uvLayer: ''
	}
];

/**
 * Available floor texture options.
 *
 * Use these keys with the `floorKey` prop in RenderKitchenProps or
 * via `controller.changeFloorTexture(key)`.
 *
 * Floor textures include diffuse maps and optional normal maps for
 * realistic surface detail. All floor textures load at 1K resolution
 * and do not have quality variants like sky textures.
 *
 * The selected floor texture is loaded as a **critical** texture
 * (Phase 1 of progressive loading), ensuring the floor displays
 * immediately when the scene renders.
 *
 * @example
 * ```typescript
 * // In React component
 * <Kitchen3d floorKey="floor/herringbone-parquet" />
 *
 * // Or via controller
 * controller.changeFloorTexture('floor/herringbone-parquet');
 * ```
 */
export const FLOOR_TEXTURES: TextureOption[] = [
	{
		key: 'floor/grid-tile',
		name: 'Grid Tile',
		description: 'Simple grid pattern tile for reference and alignment',
		texture: 'grid-tile.png'
	},
	{
		key: 'floor/textured-beige',
		name: 'Textured Beige',
		description: 'Neutral beige textured floor surface',
		texture: 'textured-beige-floor.jpg'
	},
	{
		key: 'floor/hide',
		name: 'Hidden',
		description: 'Solid color floor without texture pattern',
		texture: ''
	},
	{
		key: 'floor/pebble-embedded-concrete',
		name: 'Pebble Embedded Concrete',
		description: 'Concrete surface with embedded pebbles and aggregate',
		texture: 'pebble_embedded_concrete_02_diff_1k.jpg'
	},
	{
		key: 'floor/asphalt-01',
		name: 'Asphalt',
		description: 'Dark asphalt surface typical of driveways and patios',
		texture: 'asphalt_01_diff_1k.jpg'
	},
	{
		key: 'floor/brick-moss-001',
		name: 'Brick with Moss',
		description: 'Weathered brick with natural moss growth in crevices',
		texture: 'brick_moss_001_diff_1k.jpg'
	},
	{
		key: 'floor/brick-pavement-02',
		name: 'Brick Pavement',
		description: 'Traditional brick pavement pattern',
		texture: 'brick_pavement_02_diff_1k.jpg'
	},
	{
		key: 'floor/concrete-pavers',
		name: 'Concrete Pavers',
		description: 'Modern concrete paver stones with clean lines',
		texture: 'concrete_pavers_diff_1k.jpg'
	},
	{
		key: 'floor/diagonal-parquet',
		name: 'Diagonal Parquet',
		description: 'Wood parquet flooring in diagonal pattern',
		texture: 'diagonal_parquet_diff_1k.jpg'
	},
	{
		key: 'floor/granite-tile',
		name: 'Granite Tile',
		description: 'Polished granite tile flooring',
		texture: 'granite_tile_diff_1k.jpg'
	},
	{
		key: 'floor/gravel-embedded-concrete',
		name: 'Gravel Embedded Concrete',
		description: 'Exposed aggregate concrete with gravel surface',
		texture: 'gravel_embedded_concrete_diff_1k.jpg'
	},
	{
		key: 'floor/herringbone-parquet',
		name: 'Herringbone Parquet',
		description: 'Classic herringbone wood parquet pattern',
		texture: 'herringbone_parquet_diff_1k.jpg'
	},
	{
		key: 'floor/herringbone-pavement',
		name: 'Herringbone Pavement',
		description: 'Brick pavement in herringbone arrangement',
		texture: 'herringbone_pavement_diff_1k.jpg'
	},
	{
		key: 'floor/large-sandstone-blocks',
		name: 'Large Sandstone Blocks',
		description: 'Natural sandstone block paving',
		texture: 'large_sandstone_blocks_01_diff_1k.jpg'
	},
	{
		key: 'floor/old-wood-floor',
		name: 'Old Wood Floor',
		description: 'Rustic aged wood plank flooring',
		texture: 'old_wood_floor_diff_1k.jpg'
	},
	{
		key: 'floor/pavement-02',
		name: 'Pavement Style 2',
		description: 'Standard pavement surface texture',
		texture: 'pavement_02_diff_1k.jpg'
	},
	{
		key: 'floor/pavement-03',
		name: 'Pavement Style 3',
		description: 'Alternative pavement surface texture',
		texture: 'pavement_03_diff_1k.jpg'
	},
	{
		key: 'floor/pavement-05',
		name: 'Pavement Style 5',
		description: 'Varied pavement surface texture',
		texture: 'pavement_05_diff_1k.jpg'
	},
	{
		key: 'floor/patterned-cobblestone',
		name: 'Patterned Cobblestone',
		description: 'Traditional cobblestone in decorative pattern',
		texture: 'patterned_cobblestone_diff_1k.jpg'
	},
	{
		key: 'floor/sparse-grass',
		name: 'Sparse Grass',
		description: 'Natural grass surface with realistic variation',
		texture: 'sparse_grass_diff_1k.jpg'
	},
	{
		key: 'floor/square-tiles',
		name: 'Square Tiles',
		description: 'Uniform square tile pattern',
		texture: 'square_tiles_02_diff_1k.jpg'
	},
	{
		key: 'floor/stone-tiles',
		name: 'Stone Tiles',
		description: 'Natural stone tile flooring',
		texture: 'stone_tiles_02_diff_1k.jpg'
	},
	{
		key: 'floor/t-brick-floor',
		name: 'T-Brick Floor',
		description: 'Brick floor in T-pattern arrangement',
		texture: 't_brick_floor_002_diffuse_1k.jpg'
	},
	{
		key: 'floor/weathered-brown-planks',
		name: 'Weathered Brown Planks',
		description: 'Weathered brown wood plank flooring',
		texture: 'weathered_brown_planks_diff_1k.jpg'
	},
	{
		key: 'floor/wood-floor',
		name: 'Wood Floor',
		description: 'Classic wood plank flooring',
		texture: 'wood_floor_diff_1k.jpg'
	},
	{
		key: 'floor/wood-floor-deck',
		name: 'Wood Floor Deck',
		description: 'Outdoor deck-style wood flooring',
		texture: 'wood_floor_deck_diff_1k.jpg'
	},
	{
		key: 'floor/wood-planks-grey',
		name: 'Grey Wood Planks',
		description: 'Grey-toned wood plank flooring',
		texture: 'wood_planks_grey_diff_1k.jpg'
	}
];
